import { PackageMinus, RotateCcw, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { ReturnStockUnitModal } from '../components/ReturnStockUnitModal';
import { StockUnitModal } from '../components/StockUnitModal';
import { WriteOffStockUnitModal } from '../components/WriteOffStockUnitModal';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';
import type { InventoryItemOption } from '../inventoryTypes';
import { OWNERSHIP_LABEL } from '../inventoryTypes';
import { stockUnitsApi } from '../stockApi';
import type { StockUnitDetail, StockUnitListItem } from '../stockTypes';
import { UNIT_STATUS, UNIT_STATUS_LABEL, unitBlockedReason, unitConditionTone } from '../stockTypes';

/**
 * Individually serial-numbered things, tracked one at a time.
 *
 * **The anchor is the SERIAL NUMBER**, for the reason the item catalog anchors on its SKU: it
 * IS the identity of a serialized thing. A serial answers the question a quantity cannot —
 * "two of the 43 laptops are broken" says nothing about which two.
 */
export function StockUnitsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<StockUnitDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [writingOff, setWritingOff] = useState<StockUnitListItem | null>(null);
  const [returning, setReturning] = useState<StockUnitListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    unit: StockUnitListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    Both feeds are permission-gated, like the batch page's and unlike the item page's: this
    module's implied reads are SCOPED to create and update, so a reader holding only
    `stock-units.read` genuinely lacks them.

    Seeded TRUE and left true on a failed feed — a request that did not come back is not
    evidence that a catalog is empty.
  */
  const [ready, setReady] = useState({ items: true, facilities: true });

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      inventoryItemsApi.options(controller.signal),
      storageFacilitiesApi.options(controller.signal),
    ])
      .then(([items, facilities]) => {
        if (controller.signal.aborted) return;

        setReady({
          // The MIRROR of the batch page's question: a quantity item cannot be recorded here,
          // so what matters is whether a serialized one exists at all.
          items: items.some((i: InventoryItemOption) => i.tracksSerial),
          facilities: facilities.length > 0,
        });
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchUnits = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      stockUnitsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          item: query.facets.item?.join(','),
          facility: query.facets.facility?.join(','),
          condition: query.facets.condition?.join(','),
        },
        signal,
      ),
    [],
  );

  const openEditor = useCallback(async (unit: StockUnitListItem) => {
    setError(null);

    try {
      setEditing(await stockUnitsApi.get(unit.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not open this unit.', 0));
    }
  }, []);

  const rowActions = useCallback(
    (unit: StockUnitListItem): MenuItem[] => [
      {
        key: `edit-${unit.id}`,
        label: 'Edit',
        permission: 'stock-units.update',
        // The server refuses an edit of a written-off unit — it would silently return it to the
        // tally with no ledger line. Saying so here beats meeting it as a 409.
        disabledReason: unit.isOnHand ? null : unitBlockedReason(unit.status, 'edit'),
        onClick: () => void openEditor(unit),
      },
      {
        key: `write-off-${unit.id}`,
        label: 'Write off',
        icon: <PackageMinus className="h-4 w-4" aria-hidden="true" />,
        permission: 'stock-units.write-off',
        disabledReason: unit.isOnHand ? null : unitBlockedReason(unit.status, 'write-off'),
        onClick: () => setWritingOff(unit),
      },
      {
        /*
          The SAME key as the write-off, not the lighter restore one: this moves stock, and
          deciding what is on the owned tally is one privilege in both directions. Offered only
          for a STOLEN unit — a disposal is a certified destruction and is not undone here.
        */
        key: `return-${unit.id}`,
        label: 'Return to stock',
        icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'stock-units.write-off',
        disabledReason:
          unit.status === UNIT_STATUS.stolen
            ? null
            : unit.status === UNIT_STATUS.disposed
              ? 'This unit was disposed of. A disposal is a certified destruction and is not undone here.'
              : 'Only a unit reported stolen can be returned to stock.',
        onClick: () => setReturning(unit),
      },
      {
        key: `restore-${unit.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'stock-units.delete',
        disabledReason: unit.isActive ? 'This unit is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', unit }),
      },
    ],
    [openEditor],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await stockUnitsApi.deactivate(confirming.unit.id);
      else await stockUnitsApi.activate(confirming.unit.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<StockUnitListItem>
        module="stock-units"
        breadcrumb={['Logistics', 'Inventory', 'Stock Units']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'condition', label: 'Condition' },
          { key: 'item', label: 'Item', permission: 'inventory-items.read' },
          { key: 'facility', label: 'Facility', permission: 'storage-facilities.read' },
        ]}
        prerequisites={[
          {
            label: 'a serial-tracked item',
            met: ready.items,
            to: '/inventory/settings/items',
            permission: 'inventory-items.read',
          },
          {
            label: 'a storage facility',
            met: ready.facilities,
            to: '/inventory/settings/storage-facilities',
            permission: 'storage-facilities.read',
          },
        ]}
        // BOTH types: a receipt and a write-off each write a unit row and a ledger line, and a
        // history blind to the ledger would show the unit changing and never say why.
        auditEntityTypes={['StockUnit', 'StockMovement']}
        rowKey={(unit) => unit.id}
        fetch={fetchUnits}
        reloadKey={reloadKey}
        searchPlaceholder="Search serial, property no., SKU, item, fund or facility"
        emptyMessage="No stock units yet. Receive one to start tracking it by serial."
        onCreate={() => setCreating(true)}
        createPermission="stock-units.create"
        onRowOpen={(unit) => navigate(`/inventory/stock-units/${unit.id}`)}
        openPermission="stock-units.read"
        rowActions={rowActions}
        onDelete={(unit) => setConfirming({ kind: 'deactivate', unit })}
        deletePermission="stock-units.delete"
        deleteLabel={() => 'Delete'}
        /*
          The batch page's rule, read through a status: deleting a unit that is still HELD takes
          it out of every tally with no ledger line saying where it went. Write it off first —
          that is the operation that records where it went.
        */
        deleteDisabledReason={(unit) =>
          !unit.isActive
            ? 'This unit is already deleted.'
            : unit.isOnHand
              ? 'This unit is still on the owned tally. Write it off — condemned or stolen — before deleting the record.'
              : null
        }
        onClearData={() => stockUnitsApi.clear()}
        clearDataPermission="stock-units.clear-data"
        moduleNoun="stock units"
        otherInfo={(unit) => unit}
      />

      {creating || editing !== null ? (
        <StockUnitModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {writingOff ? (
        <WriteOffStockUnitModal
          open
          unit={writingOff}
          onClose={() => setWritingOff(null)}
          onSaved={() => {
            setWritingOff(null);
            refresh();
          }}
        />
      ) : null}

      {returning ? (
        <ReturnStockUnitModal
          open
          unit={returning}
          onClose={() => setReturning(null)}
          onSaved={() => {
            setReturning(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete unit' : 'Restore unit'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`stock-unit-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Unit "${confirming.unit.serialNumber}" stops appearing in this list.`
                  : `Unit "${confirming.unit.serialNumber}" appears in this list again.`}
              </p>

              {confirming.kind === 'activate' ? (
                <p>
                  Its serial is re-checked first. Deleting a unit FREES its serial for another
                  row of the same item to take, so a restore can collide — unlike an item's SKU,
                  which is never freed.
                </p>
              ) : null}

              <p>
                The ledger is untouched either way — a movement is never deleted, so this unit's
                history survives whatever happens to the row.
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}


const COLUMNS: ColumnDef<StockUnitListItem>[] = [
  {
    key: 'serialNumber',
    header: 'Serial',
    width: 190,
    sortable: true,
    render: (unit) => <TextCell value={unit.serialNumber} mono />,
  },
  {
    /*
      OURS, where the serial beside it is the manufacturer's — and it is what an ICS or a
      PAR is read down, so it sits next to the identity rather than out among the dates.

      NOT SORTABLE: the server's allow-list does not name it, and a header that silently
      falls back to the default sort reads as a broken control.
    */
    key: 'inventoryNumber',
    header: 'Property no.',
    width: 160,
    render: (unit) => <TextCell value={unit.inventoryNumber} mono />,
  },
  {
    key: 'sku',
    header: 'SKU',
    width: 150,
    sortable: true,
    summary: true,
    render: (unit) => <TextCell value={unit.sku} mono />,
  },
  {
    key: 'itemName',
    header: 'Item',
    width: 240,
    flex: true,
    sortable: true,
    summary: true,
    render: (unit) => <TextCell value={unit.itemName} />,
  },
  {
    key: 'storageFacilityName',
    header: 'Facility',
    width: 180,
    sortable: true,
    summary: true,
    render: (unit) => <TextCell value={unit.storageFacilityName} />,
  },
  {
    key: 'ownership',
    header: 'Held as',
    width: 120,
    // An em dash where the unit defers to the catalog item — "not stated here" is a different
    // fact from "owned", and the dash is what says which.
    render: (unit) => (
      <TextCell value={unit.ownership === null ? null : OWNERSHIP_LABEL[unit.ownership]} />
    ),
  },
  {
    key: 'acquisitionDate',
    header: 'Acquired',
    width: 130,
    sortable: true,
    // A CALENDAR date — `formatDateOnly` through a mono cell, never `<DateCell>`.
    render: (unit) => (
      <TextCell
        value={unit.acquisitionDate === null ? null : formatDateOnly(unit.acquisitionDate)}
        mono
      />
    ),
  },
  {
    // What the purchase was charged to, beside when it was made. Not sortable, and not mono:
    // a fund is a name somebody typed rather than a machine value.
    key: 'fund',
    header: 'Fund',
    width: 160,
    render: (unit) => <TextCell value={unit.fund} />,
  },
  {
    key: 'termEndDate',
    header: 'Term ends',
    width: 130,
    sortable: true,
    render: (unit) => (
      <TextCell value={unit.termEndDate === null ? null : formatDateOnly(unit.termEndDate)} mono />
    ),
  },
  {
    key: 'unitCost',
    header: 'Unit cost',
    width: 130,
    align: 'right',
    render: (unit) => (
      <TextCell value={unit.unitCost === null ? null : formatMoney(unit.unitCost)} mono numeric />
    ),
  },
  {
    // The unit's CONDITION, a different axis from whether the row is deleted — so `status: true`
    // belongs to the column below it.
    key: 'condition',
    header: 'Condition',
    width: 140,
    render: (unit) => (
      <StatusText tone={unitConditionTone(unit.status)}>{UNIT_STATUS_LABEL[unit.status]}</StatusText>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (unit) =>
      unit.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
